import { useState, useEffect, useCallback, type RefObject } from 'react';

export interface UseVirtualListOptions {
  itemCount: number;
  itemHeight?: number;
  overscan?: number;
  containerRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
}

export interface VirtualListResult {
  startIndex: number;
  endIndex: number;
  topPadding: number;
  bottomPadding: number;
  isVirtual: boolean;
}

export function calculateVirtualRange({
  itemCount,
  itemHeight = 72,
  overscan = 20,
  scrollTop = 0,
  viewportHeight = 600,
  enabled = false,
}: {
  itemCount: number;
  itemHeight?: number;
  overscan?: number;
  scrollTop?: number;
  viewportHeight?: number;
  enabled?: boolean;
}): VirtualListResult {
  if (!enabled || itemCount <= 200) {
    return {
      startIndex: 0,
      endIndex: itemCount,
      topPadding: 0,
      bottomPadding: 0,
      isVirtual: false,
    };
  }

  const rawStart = Math.floor(scrollTop / itemHeight) - overscan;
  const startIndex = Math.max(0, rawStart);

  const visibleCount = Math.ceil(viewportHeight / itemHeight);
  const rawEnd = Math.floor(scrollTop / itemHeight) + visibleCount + overscan;
  const endIndex = Math.min(itemCount, rawEnd);

  const topPadding = startIndex * itemHeight;
  const bottomPadding = Math.max(0, (itemCount - endIndex) * itemHeight);

  return {
    startIndex,
    endIndex,
    topPadding,
    bottomPadding,
    isVirtual: true,
  };
}

/**
 * Pure React lightweight list virtualization hook.
 * Avoids any external dependency like react-window.
 * Only activates when `enabled` is true (e.g. itemCount > 200).
 */
export function useVirtualList({
  itemCount,
  itemHeight = 72,
  overscan = 20,
  containerRef,
  enabled = false,
}: UseVirtualListOptions): VirtualListResult {
  const [scrollState, setScrollState] = useState({ scrollTop: 0, viewportHeight: 600 });

  const updateScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setScrollState({
      scrollTop: el.scrollTop,
      viewportHeight: el.clientHeight || 600,
    });
  }, [containerRef]);

  useEffect(() => {
    if (!enabled) return;

    const el = containerRef.current;
    if (!el) return;

    updateScroll();

    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updateScroll();
        rafId = null;
      });
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [enabled, containerRef, updateScroll]);

  return calculateVirtualRange({
    itemCount,
    itemHeight,
    overscan,
    scrollTop: scrollState.scrollTop,
    viewportHeight: scrollState.viewportHeight,
    enabled,
  });
}
